import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Stack,
  IconButton,
  Tooltip,
  useTheme,
  alpha,
} from "@mui/material";
import {
  Phone,
  PhoneCall,
  PhoneMissed,
  Clock,
  ArrowDownLeft,
  ArrowUpRight,
  Filter,
  Download,
  Calendar,
  Search,
  Zap,
  Signal,
  RotateCcw,
} from "lucide-react";
import { Link } from "react-router-dom";
import axiosInstance from "../../../services/axiosInstance";
import apiEndPointsConfig from "../../../services/apiEndPoints";

const StatCard = ({ title, value, icon: Icon, color }) => {
  const theme = useTheme();
  return (
    <Card
      sx={{
        height: "100%",
        borderRadius: 4,
        boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
        background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(color, 0.05)} 100%)`,
        border: `1px solid ${alpha(color, 0.1)}`,
      }}
    >
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              p: 1.5,
              borderRadius: 3,
              bgcolor: alpha(color, 0.1),
              color: color,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon size={24} />
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              {title}
            </Typography>
            <Typography variant="h5" fontWeight={700}>
              {value}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

const ClientCallDetails = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split("T")[0];
  });
  const [endDate, setEndDate] = useState(new Date().toISOString().split("T")[0]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(apiEndPointsConfig.getClientCallDetails, {
        params: { startDate, endDate },
      });
      if (response.data.success) {
        setData(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching client call details:", error);
      alert("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleFilter = () => {
    fetchData();
  };

  const handleClear = () => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    const start = d.toISOString().split("T")[0];
    const end = new Date().toISOString().split("T")[0];
    
    setSearchTerm("");
    setStartDate(start);
    setEndDate(end);
    
    // Fetch with cleared values
    setLoading(true);
    axiosInstance.get(apiEndPointsConfig.getClientCallDetails, {
      params: { startDate: start, endDate: end },
    })
    .then(response => {
      if (response.data.success) {
        setData(response.data.data);
      }
    })
    .catch(error => {
      console.error("Error fetching client call details:", error);
      alert("Failed to fetch data");
    })
    .finally(() => {
      setLoading(false);
    });
  };

  const filteredData = data.filter((item) =>
    (item.clientName || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.clientEmail || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totals = data.reduce(
    (acc, curr) => ({
      totalCalls: acc.totalCalls + curr.totalCalls,
      connectedCalls: acc.connectedCalls + curr.connectedCalls,
      notConnectedCalls: acc.notConnectedCalls + curr.notConnectedCalls,
      totalMinutes: acc.totalMinutes + curr.totalMinutes,
      connectedMinutes: acc.connectedMinutes + curr.connectedMinutes,
      inboundCalls: acc.inboundCalls + curr.inboundCalls,
      outboundCalls: acc.outboundCalls + curr.outboundCalls,
      sipCalls: acc.sipCalls + curr.sipCalls,
      gsmCalls: acc.gsmCalls + curr.gsmCalls,
      sipMinutes: acc.sipMinutes + curr.sipMinutes,
      gsmMinutes: acc.gsmMinutes + curr.gsmMinutes,
    }),
    {
      totalCalls: 0,
      connectedCalls: 0,
      notConnectedCalls: 0,
      totalMinutes: 0,
      connectedMinutes: 0,
      inboundCalls: 0,
      outboundCalls: 0,
      sipCalls: 0,
      gsmCalls: 0,
      sipMinutes: 0,
      gsmMinutes: 0,
    }
  );

  return (
    <Box sx={{ pb: 5 }}>
      {/* Filters Section */}
      <Paper
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 4,
          boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
        }}
      >
        <Grid container spacing={3} alignItems="flex-end">
          <Grid item xs={12} md={3}>
            <Typography variant="body2" fontWeight={600} mb={1}>
              Search Client
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <Search size={18} style={{ marginRight: 8, color: theme.palette.text.secondary }} />
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Typography variant="body2" fontWeight={600} mb={1}>
              Start Date
            </Typography>
            <TextField
              fullWidth
              type="date"
              size="small"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Typography variant="body2" fontWeight={600} mb={1}>
              End Date
            </Typography>
            <TextField
              fullWidth
              type="date"
              size="small"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ height: 40 }}>
              <Button
                variant="contained"
                startIcon={<Filter size={18} />}
                onClick={handleFilter}
                sx={{
                  px: 3,
                  borderRadius: 2,
                  bgcolor: "#6366F1",
                  "&:hover": { bgcolor: "#4F46E5" },
                  textTransform: "none",
                  fontWeight: 600,
                  whiteSpace: "nowrap"
                }}
              >
                Apply
              </Button>
              <Button
                variant="outlined"
                startIcon={<RotateCcw size={18} />}
                onClick={handleClear}
                sx={{
                  px: 3,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  borderColor: "#94A3B8",
                  color: "#64748B",
                  "&:hover": { borderColor: "#64748B", bgcolor: alpha("#94A3B8", 0.05) },
                  whiteSpace: "nowrap"
                }}
              >
                Clear
              </Button>
              <Button
                variant="outlined"
                startIcon={<Download size={18} />}
                onClick={() => {
                  if (filteredData.length === 0) {
                    alert("No data to export");
                    return;
                  }

                  const headers = [
                    "Client Name",
                    "Email",
                    "Total Calls",
                    "Connected",
                    "Not Connected",
                    "SIP Calls",
                    "GSM Calls",
                    "Inbound",
                    "Outbound",
                    "Total Minutes",
                    "Connected Minutes",
                    "SIP Minutes",
                    "GSM Minutes"
                  ];

                  const csvData = filteredData.map(row => [
                    `"${row.clientName || ""}"`,
                    `"${row.clientEmail || ""}"`,
                    row.totalCalls,
                    row.connectedCalls,
                    row.notConnectedCalls,
                    row.sipCalls,
                    row.gsmCalls,
                    row.inboundCalls,
                    row.outboundCalls,
                    row.totalMinutes,
                    row.connectedMinutes,
                    row.sipMinutes,
                    row.gsmMinutes
                  ]);

                  const csvContent = [
                    headers.join(","),
                    ...csvData.map(e => e.join(","))
                  ].join("\n");

                  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement("a");
                  link.setAttribute("href", url);
                  link.setAttribute("download", `client_call_details_${startDate}_to_${endDate}.csv`);
                  link.style.visibility = "hidden";
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                sx={{
                  px: 3,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  borderColor: "#6366F1",
                  color: "#6366F1",
                  whiteSpace: "nowrap"
                }}
              >
                Export CSV
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Summary Cards */}
      {/* Summary Cards Row 1 */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard title="Total Calls" value={totals.totalCalls} icon={Phone} color="#6366F1" />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard title="Connected" value={totals.connectedCalls} icon={PhoneCall} color="#10B981" />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard title="Not connected" value={totals.notConnectedCalls} icon={PhoneMissed} color="#F43F5E" />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard title="Inbound" value={totals.inboundCalls} icon={ArrowDownLeft} color="#8B5CF6" />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard title="Outbound" value={totals.outboundCalls} icon={ArrowUpRight} color="#EC4899" />
        </Grid>
      </Grid>

      {/* Summary Cards Row 2 */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard title="SIP Count (04)" value={totals.sipCalls} icon={Zap} color="#06B6D4" />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard title="GSM Count (05)" value={totals.gsmCalls} icon={Signal} color="#F97316" />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard title="SIP Minutes" value={totals.sipMinutes.toFixed(1)} icon={Clock} color="#0EA5E9" />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard title="GSM Minutes" value={totals.gsmMinutes.toFixed(1)} icon={Clock} color="#D946EF" />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard title="Total Mins" value={totals.totalMinutes.toFixed(1)} icon={Clock} color="#F59E0B" />
        </Grid>
      </Grid>

      {/* Data Table */}
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 4,
          boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
          overflow: "hidden",
        }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
            <CircularProgress size={40} sx={{ color: "#6366F1" }} />
          </Box>
        ) : (
          <Table sx={{ minWidth: 650 }}>
            <TableHead sx={{ bgcolor: alpha("#6366F1", 0.05) }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, color: "#475569" }}>Client Name</TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#475569" }}>Email</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: "#475569" }}>Total Calls</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: "#475569" }}>Connected</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: "#475569" }}>Not connected</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: "#475569" }}>SIP (04/4)</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: "#475569" }}>GSM (05/5)</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: "#475569" }}>Inbound</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: "#475569" }}>Outbound</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: "#475569" }}>Total (Mins)</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: "#475569" }}>Conn. (Mins)</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: "#475569" }}>Total (Hrs)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.length > 0 ? (
                filteredData.map((row) => (
                  <TableRow
                    key={row.clientId}
                    sx={{
                      "&:hover": { bgcolor: alpha("#6366F1", 0.02) },
                      transition: "background-color 0.2s",
                    }}
                  >
                    <TableCell sx={{ fontWeight: 600 }}>
                      <Link
                        to={`/admin/details/${row.clientId}`}
                        style={{ textDecoration: "none", color: theme.palette.primary.main }}
                      >
                        {row.clientName}
                      </Link>
                    </TableCell>
                    <TableCell color="text.secondary">{row.clientEmail}</TableCell>
                    <TableCell align="center">
                      <Typography fontWeight={600}>{row.totalCalls}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography color="#10B981" fontWeight={600}>{row.connectedCalls}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography color="#F43F5E" fontWeight={600}>{row.notConnectedCalls}</Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, color: "#06B6D4" }}>{row.sipCalls}</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, color: "#F97316" }}>{row.gsmCalls}</TableCell>
                    <TableCell align="center">{row.inboundCalls}</TableCell>
                    <TableCell align="center">{row.outboundCalls}</TableCell>
                    <TableCell align="center">{row.totalMinutes}</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, color: "#0EA5E9" }}>{row.connectedMinutes}</TableCell>
                    <TableCell align="center">{row.totalHours}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary">No client data found for the selected range.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </Box>
  );
};

export default ClientCallDetails;
